/**
 * The tutor's presentation (doc 19) — the run screen's seam onto onboarding.
 *
 * The split is the same one the world and rim views use: `Tutor` decides WHAT
 * to say and when, this decides where it lands on screen, and `ui/nudge.ts`
 * draws the pixels. The run screen owns none of it beyond calling `feed`,
 * `teach` and `draw`.
 */

import { PixelSurface } from '../render/surface.ts';
import type { Sim } from '../sim/sim.ts';
import type { Command, SimEvent } from '../sim/types.ts';
import { drawNudge, nudgeRect } from '../ui/nudge.ts';
import { contains, type Rect } from '../ui/widgets.ts';
import type { Tutor } from './tutor.ts';

/** What the view needs from the run screen, and nothing more. */
export interface NudgeHost {
  readonly sim: Sim;
  uiScale(): number;
  /** Doc 18 §3: a blinking target becomes a steady one, never an absent one. */
  reducedMotion(): boolean;
  tileClient(i: number, j: number): { x: number; y: number };
  shoalClient(id: string): { x: number; y: number } | null;
  say(text: string, ms: number): void;
  persist(): void;
}

/** Doc 19: a 2-frame blink, ~4 Hz. Pixel-honest, never a smooth glow. */
const BLINK_MS = 260;

export class NudgeView {
  private readonly tutor: Tutor | null;
  private readonly host: NudgeHost;
  private batch: SimEvent[] = [];
  private rect: Rect | null = null;
  private cardId: string | null = null;

  constructor(tutor: Tutor | null, host: NudgeHost) {
    this.tutor = tutor;
    this.host = host;
  }

  get armed(): boolean {
    return this.tutor !== null;
  }

  /** Doc 19: null passes the command through, a string is the toast instead. */
  allows(cmd: Command): string | null {
    return this.tutor?.allows(cmd) ?? null;
  }

  feed(events: SimEvent[]): void {
    if (this.tutor) this.batch.push(...events);
  }

  noteInput(now: number): void {
    this.tutor?.noteInput(now);
  }

  /** One pass per frame. Returns true when the card on screen changed. */
  teach(now: number, selectedStructId: number): boolean {
    const tutor = this.tutor;
    if (!tutor) return false;
    tutor.observe(this.host.sim, this.batch, { selectedStructId }, now);
    this.batch = [];
    const id = tutor.card?.id ?? null;
    if (id === this.cardId) return false;
    this.cardId = id;
    this.rect = null;
    this.host.persist(); // seen[] and released[] are progress, banked like Pearls
    return true;
  }

  /** A click at UI (x, y), or ESC. True when it consumed the input. */
  dismiss(x?: number, y?: number): boolean {
    if (!this.tutor?.card) return false;
    if (x !== undefined && y !== undefined && !(this.rect && contains(this.rect, x, y))) return false;
    const line = this.tutor.dismiss();
    if (line) this.host.say(line, 3200);
    this.cardId = this.tutor.card?.id ?? null;
    this.rect = null;
    this.host.persist();
    return true;
  }

  /** The G2 ring on the minimap's rim ring, blinking with the world pulse. */
  minimapPulse(now: number): { shoalId: string; on: boolean } | null {
    const id = this.tutor?.card?.shoalId;
    return id ? { shoalId: id, on: this.host.reducedMotion() || blink(now) } : null;
  }

  /** Draws the card and its target pulse. True when the frame must repaint. */
  draw(s: PixelSurface, now: number, hotbarTop: number): boolean {
    const card = this.tutor?.card;
    if (!card) {
      this.rect = null;
      return false;
    }
    const draw = {
      title: card.title,
      body: card.body,
      gate: card.gate !== null,
      targets: this.targets(),
      parity: (this.host.reducedMotion() || blink(now) ? 0 : 1) as 0 | 1,
      hotbarTop,
    };
    this.rect = nudgeRect(s, draw);
    drawNudge(s, draw);
    return true; // the pulse is a blink: the HUD stays dirty while it is up
  }

  /** UI-space points the open card points at (doc 19 §The two gates). */
  private targets(): Array<{ x: number; y: number }> {
    const card = this.tutor?.card;
    if (!card?.target) return [];
    const u = this.host.uiScale();
    if (card.target === 'veins') {
      return this.host.sim.scenario.map.oreVeins.map(([i, j]) => {
        const p = this.host.tileClient(i!, j!);
        return { x: p.x / u, y: p.y / u };
      });
    }
    const p = card.shoalId ? this.host.shoalClient(card.shoalId) : null;
    return p ? [{ x: p.x / u, y: p.y / u }] : [];
  }
}

function blink(now: number): boolean {
  return Math.floor(now / BLINK_MS) % 2 === 0;
}
